<template>
  <div class="dsr-published-app-container">
    <TransitionFadeInOut>
      <UniversalLoader
        v-if="loadingRoom"
        class="loading"
        :message="t('dsr_published_loading_your_session')"
      />
      <NotFound v-else-if="error || inputOrEntityError" />
      <UniversalLoader
        v-else-if="loadingDsrAndParticipants || loadingInputAndEntity || !dsr"
        class="loading"
        :message="t('loading_your_dsr')"
      />
      <div v-else id="published-view" class="published-wrapper">
        <div class="topbar-wrapper">
          <TransitionSlideAndFadeInOut :origin="SlideOrigin.Top">
            <RevisionPromptBar
              v-if="showRevisionPromptBar"
              class="revision-prompt-bar"
              :sender="senderFullName"
              :time-left="forcedReviewTimer"
              :loading="loading"
              :is-dsr="true"
              @click="loadUpdatedContent"
            />
          </TransitionSlideAndFadeInOut>
          <TopBar
            :theme="theme"
            :current-view="currentView"
            :is-small-screen="!showLeftSide"
            :participants="participants"
            :pages="visiblePages"
            :previous-page-id="previousPageId"
            :next-page-id="nextPageId"
            :current-page-id="currentPageId"
            :participant="participant"
            :owner="owner"
            :show-contextual-commenting="showContextualCommenting"
            :action-plan-visible="actionPlanVisible"
            :feature-settings="featureSettings"
            :is-allowed-to-invite="isAllowedToInvite"
            :is-public-room="isPublicRoom"
            @show-share-modal="showShareModal = true"
            @show-participant-overview="participantsOverviewIsVisible = true"
            @set-view="setView"
            @show-feed="updateFeedVisible = true"
            @open-mobile-navigation="showMobileNavigation = true"
            @select-section="navigateToSection"
            @select-page="navigateToPage"
            @go-to-comment="navigateToComment"
          />
          <NewSlideNavigationMenu
            v-if="showMobileNavigation"
            :is-allowed-to-invite="isAllowedToInvite"
            :is-public-room="isPublicRoom"
            @close-mobile-navigation="showMobileNavigation = false"
            @show-participant-overview="
              showMobileNavigation = false;
              participantsOverviewIsVisible = true;
            "
            @show-share-modal="
              showMobileNavigation = false;
              showShareModal = true;
            "
            @navigate-to="
              showMobileNavigation = false;
              setView($event);
            "
          />
        </div>
        <div class="main">
          <TransitionSlideInOut appear :origin="SlideOrigin.Right">
            <UpdateDrawer
              v-if="updateFeedVisible || hasUnreadPosts"
              :participants="participants"
              :participant-id="participant.id"
              :owner="owner"
              @close="
                updateFeedVisible = false;
                setPostsAsSeen();
              "
            />
          </TransitionSlideInOut>
          <Chat
            v-if="currentView === DrView.Chat"
            :loaded="isChatLoaded"
            :owner="owner"
            :owner-company-name="ownerCompanyName"
            :participant-id="participant.id"
          />
          <MutualActionPlan
            v-else-if="currentView === DrView.MutualActionPlan"
            :owner="owner"
            :participant-id="participant.id"
          />
          <Files
            v-else-if="currentView === DrView.Files"
            :owner="owner"
            :participant-id="participant.id"
          />
          <div
            v-else-if="currentView === DrView.Presentation"
            id="published-view-main-content"
            class="presentation"
          >
            <SidebarOuterWrapper v-if="showLeftSide" class="static-navigation-bar left-container">
              <PageNavigationBar
                :pages="visiblePages"
                :current-page-id="currentPageId"
                :participant="participant"
                :locked-page-ids="lockedPageIds"
                @select="navigateToPage"
                @navigate-to-section="navigateToSection"
              >
                <template #top>
                  <NameControl :name="dsr.name" :company-name="dsrCompanyName" />
                </template>
              </PageNavigationBar>
            </SidebarOuterWrapper>
            <div
              :class="[
                'center-container',
                {
                  'no-side-padding': showMobileView,
                  'comments-active': commentsActive,
                },
              ]"
            >
              <div class="editor-wrapper-container">
                <div ref="editorContainer" class="editor-wrapper">
                  <div>
                    <hr class="top-filler" />
                  </div>
                  <EditAfterSendNotification
                    v-if="currentPageLocked"
                    class="edit-after-send-notification"
                    data-external="dr-published-view-edit-after-send-notification"
                    :header="t('dr_cc_dealroom_owner_is_updating_the_dealroom_header')"
                    :description="t('dr_cc_dealroom_owner_updating_dealroom_description')"
                  />
                  <EditorWrapper
                    v-if="currentPage"
                    :key="currentPage.id"
                    :auth-id="authId"
                    :page="currentPage"
                    :token="token"
                    :participant="participant"
                    :dsr="dsr"
                    :pages="visiblePages"
                    :entity="{ ...entity, theme }"
                    :participant-input="participantInput"
                    :show-contextual-commenting="showContextualCommenting"
                    :commenting-locked="currentPageLocked"
                    @update-participant-input="updateParticipantInput"
                    @fetch-new-image="loadEditorImageURL"
                  />
                  <NavigationButtons
                    v-if="!showLeftSide"
                    :previous-page-id="previousPageId"
                    :next-page-id="nextPageId"
                    :mobile-view="showMobileView"
                    @goto-page="navigateToPage"
                  />
                </div>
                <ContextualCommenting
                  v-if="showFloatingComments"
                  :current-page="currentPage"
                  :participants="participants"
                  :participant="participant"
                  :sender="owner"
                  :dimensions-updating="dimensionsUpdating"
                  :show-floating-comments="showRightSide"
                  :loading-view="loadingDimensionsForNewView"
                  @scroll-to-comment="scrollToComment"
                />
              </div>
            </div>
          </div>
        </div>
        <ParticipantsOverviewModal
          v-if="participantsOverviewIsVisible"
          :participants="participants"
          @close="participantsOverviewIsVisible = false"
        />
        <ShareModal
          v-if="showShareModal"
          :universal-link="universalLink"
          :current-participants="participants"
          :room-name="dsrName"
          :sender-name="owner.firstName"
          @invite-success="handleInviteSuccess"
          @close="showShareModal = false"
        />
        <InviteSuccessModal
          v-if="showInviteSuccessModal"
          :invitation="invitation"
          :sender-name="senderFullName"
          @close="handleCloseInviteSuccessModal"
          @invite-more="handleInviteMore"
        />
        <UploadingPrompt
          v-if="totalFilesToUpload > 0"
          :total-files-to-upload="totalFilesToUpload"
          :files-uploaded="filesUploaded"
        />
      </div>
    </TransitionFadeInOut>
  </div>
</template>
<script lang="ts">
import type { Participant } from '@getaccept/dsr-shared-new';
import {
  AccessRule,
  ContentNavigationHelper,
  DrView,
  DsrConstants,
  NavigationButtons,
  SidebarOuterWrapper,
  hiddenSectionsOrEmptyContractLinks,
} from '@getaccept/dsr-shared-new';
import NameControl from '@getaccept/dsr-shared-new/src/components/NameControl.vue';
import UniversalLoader from '@getaccept/lib-shared-new/src/components/UniversalLoader.vue';
import { useLayout } from '@getaccept/dsr-shared-new/src/composables/layout.composable';
import EditAfterSendNotification from '@getaccept/dsr-shared-new/src/contextual-commenting/components/EditAfterSendNotification.vue';
import { useDimensionsUpdating } from '@getaccept/dsr-shared-new/src/contextual-commenting/composables/dimensions-updated.composable';
import { useContextualCommentingStore } from '@getaccept/dsr-shared-new/src/contextual-commenting/store/contextual-commenting.store';
import { SlideOrigin } from '@getaccept/fried-tofu';
import { useScrollToComment } from '@getaccept/dsr-shared-new/src/contextual-commenting/composables/scroll-to-comment.composable';
import { AvatarHelper } from '@getaccept/lib-shared-new/src/helpers/avatars.helper';
import { useI18n } from 'vue-i18n';
import RevisionPromptBar from '@getaccept/lib-shared-new/src/prompt-bar/RevisionPromptBar.vue';
import { useToast } from '@getaccept/lib-shared-new/src/toast';
import type { Channel } from '@getaccept/pusher';
import { DealroomEvent } from '@getaccept/pusher';
import type { RemovableRef } from '@vueuse/core';
import { useSessionStorage as useCoreSessionStorage } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type { ComputedRef, Ref } from 'vue';
import { computed, defineComponent, nextTick, onMounted, ref, watch } from 'vue';
import { useFeatureStore } from '@getaccept/lib-shared-new/src/feature-restriction/store/feature.store';
import { useRoute, useRouter } from 'vue-router';
import { Feature } from '@getaccept/lib-shared-new/src/feature-restriction/types/feature';
import UploadingPrompt from '@getaccept/dsr-shared-new/src/files/components/UploadingPrompt.vue';
import { useLaunchDarkly } from '@getaccept/lib-shared-new/src/launch-darkly/launch-darkly.composable';
import { AnalyticsCompanyTrait } from '@getaccept/lib-shared-new/src/types/analytics';
import { LaunchDarklyContextKind } from '@getaccept/lib-shared-new/src/launch-darkly/types/enums/launch-darkly-context-kind';
import { basicLogger } from 'launchdarkly-js-client-sdk';
import { useLoadFeatures } from '../published/features/composables/load-features.composable';
import { pusherClient } from '../api/pusher/pusher-client';
import { useData } from '../composables/use-data.composable';
import ContextualCommenting from '../contextual-commenting/components/ContextualCommenting.vue';
import { useContextualCommentingData } from '../contextual-commenting/composables/contextual-commenting-data.composable';
import { useContextualCommentingPusher } from '../contextual-commenting/composables/contextual-commenting-pusher.composable';
import { useEntityStore } from '../entity/store/entity.store';
import Chat from '../published/chat/components/Chat.vue';
import { useChatPublishedStore } from '../published/chat/store/chat-published.store';
import PageNavigationBar from '../published/components/PageNavigationBar.vue';
import ParticipantsOverviewModal from '../published/components/ParticipantsOverviewModal.vue';
import TopBar from '../published/components/TopBar.vue';
import ShareModal from '../published/components/ShareModal.vue';
import UpdateDrawer from '../published/components/UpdateDrawer.vue';
import { useLoadDSR } from '../published/composables/load-dsr.composable';
import EditorWrapper from '../published/editor-wrapper/components/EditorWrapper.vue';
import { useTracking } from '../published/editor-wrapper/tracking/composables/tracking.composable';
import { usePublishedFeedStore } from '../published/feed/store/feed.store';
import Files from '../published/files/components/Files.vue';
import { usePublishedFileStore } from '../published/files/store/file.store';
import MutualActionPlan from '../published/mutual-action-plan/components/MutualActionPlan.vue';
import { usePublishedActionPlanStore } from '../published/mutual-action-plan/store/action-plan.store';
import NotFound from '../published/not-found/components/NotFound.vue';
import { useParticipantsStore } from '../published/store/participants.store';
import { useRevisionsStore } from '../published/store/revisions.store';
import { usePusherStore } from '../pusher/store/pusher.store';
import { useSessionStore } from '../session/store/session.store';
import type { InvitePayload } from '../published/types/participant-invite';
import InviteSuccessModal from '../published/components/InviteSuccessModal.vue';
import NewSlideNavigationMenu from './NewSlideNavigationMenu.vue';

export default defineComponent({
  name: 'PublishedView',
  components: {
    EditorWrapper,
    PageNavigationBar,
    UniversalLoader,
    NotFound,
    RevisionPromptBar,
    ParticipantsOverviewModal,
    SidebarOuterWrapper,
    NavigationButtons,
    Chat,
    MutualActionPlan,
    TopBar,
    ShareModal,
    UpdateDrawer,
    NewSlideNavigationMenu,
    NameControl,
    ContextualCommenting,
    EditAfterSendNotification,
    Files,
    UploadingPrompt,
    InviteSuccessModal,
  },
  setup() {
    const { t } = useI18n();
    const participantsOverviewIsVisible = ref(false);
    const updateFeedVisible = ref(false);
    const showShareModal = ref(false);
    const participantsStore = useParticipantsStore();

    const participant: ComputedRef<Participant> = computed(() =>
      participantsStore.participants.find(participant => participant.id === participantId.value)
    );

    const participants: ComputedRef<Participant[]> = computed(() => participantsStore.participants);
    const { showMobileView, showRightSide, showLeftSide } = useLayout();

    const dsrName = computed(() => dsr.value?.name || t('untitled_room'));
    const dsrCompanyName = computed(() => dsr.value?.details?.companyName || '');

    const sessionStore = useSessionStore();
    const { authId, participantId, token, documentId, tokenLoaded } = storeToRefs(sessionStore);

    const {
      setCurrentPage,
      currentPage,
      currentPageId,
      loading,
      dsr,
      pages,
      error,
      theme,
      loadDSR,
      loadNewPages,
      loadTheme,
      loadEditorImageURL,
      loadRoomSettings,
    } = useLoadDSR();

    const {
      participantInput,
      loadParticipantInput,
      updateParticipantInput,
      loading: loadingInputAndEntity,
      error: inputOrEntityError,
    } = useData();

    const revisionsStore = useRevisionsStore();
    const { showRevisionPromptBar, forcedReviewTimer, loadNewContent } = storeToRefs(
      revisionsStore
    );

    const actionPlanStore = usePublishedActionPlanStore();
    const { actionPlans, actionPlanVisible } = storeToRefs(actionPlanStore);

    const fileStore = usePublishedFileStore();
    const { totalFilesToUpload, filesUploaded } = storeToRefs(fileStore);

    const loadingRoom = computed(() => !documentId.value || !tokenLoaded.value);
    const featureSettings = computed(() => dsr.value?.settings?.featureSettings);
    const visiblePages = computed(() => pages.value?.filter(hiddenSectionsOrEmptyContractLinks));

    const currentPageIndex = computed(() =>
      visiblePages.value.findIndex(page => page.id === currentPageId.value)
    );

    const previousPageId = computed(() => {
      const index = currentPageIndex.value;
      return index > 0 ? visiblePages.value[index - 1].id : undefined;
    });

    const nextPageId = computed(() => {
      const index = currentPageIndex.value;
      return index < visiblePages.value.length - 1 ? visiblePages.value[index + 1].id : undefined;
    });

    const loadingDsrAndParticipants = ref(true);

    const { universalLink, owner, senders } = storeToRefs(participantsStore);

    const isPublicRoom = computed(() => dsr.value?.access === AccessRule.Open);

    const currentView: RemovableRef<DrView> = useCoreSessionStorage<DrView>(
      'currentPublishedView',
      DrView.Presentation
    );

    const currentViewComputed = computed(() => currentView.value);

    const { entity } = storeToRefs(useEntityStore());

    const ownerCompanyName = computed(() => entity.value?.companyName || entity.value?.name);

    const { setDealroomView, trackView } = useTracking();

    const setView = (view: DrView) => {
      currentView.value = view;
      setDealroomView(view);
    };

    const chatStore = useChatPublishedStore();
    const { conversations } = storeToRefs(chatStore);

    const isChatLoaded = ref(false);

    const loadChat = async () => {
      await chatStore.loadConversationList();
      await chatStore.loadConversationDetails();
      chatStore.setDefaultConversation();

      isChatLoaded.value = true;
    };

    const invitation: Ref<InvitePayload> = ref(null);
    const showInviteSuccessModal = ref(false);

    const handleInviteSuccess = (payload: InvitePayload) => {
      showShareModal.value = false;
      if (payload.participants.length === 0) {
        return;
      }

      invitation.value = payload;
      showInviteSuccessModal.value = true;
    };

    const handleCloseInviteSuccessModal = () => {
      showInviteSuccessModal.value = false;
      invitation.value = null;
    };

    const handleInviteMore = () => {
      handleCloseInviteSuccessModal();
      showShareModal.value = true;
    };

    const feedStore = usePublishedFeedStore();
    const { posts } = storeToRefs(feedStore);

    const hasUnreadPosts = computed(() => {
      if (!posts.value.length) {
        return false;
      }

      return posts.value.some(post => !post.seenBy.includes(participant.value.id));
    });

    const setPostsAsSeen = () => {
      const unseenPosts = posts.value.filter(post => !post.seenBy.includes(participant.value.id));
      if (!unseenPosts.length) {
        return;
      }
      unseenPosts.forEach(post => feedStore.setPostSeen(post.id, participant.value.id));
    };

    const loadPosts = () => {
      feedStore.init();
      feedStore.load();
    };

    const pusherStore = usePusherStore();
    const { channels } = storeToRefs(pusherStore);

    const addPusherListeners = (channel: Channel) => {
      pusherClient.listen(channel, DealroomEvent.FeatureSettingsUpdated, loadRoomSettings);

      pusherClient.listen(channel, DealroomEvent.Published, () => {
        revisionsStore.handlePusherEvent();
      });

      [DealroomEvent.PostCreated, DealroomEvent.PostDeleted, DealroomEvent.PostUpdated].forEach(
        event => {
          pusherClient.listen(channel, event, e => {
            if (e) {
              loadPosts();
            }
          });
        }
      );
    };

    const { openConversationPayload } = storeToRefs(useChatPublishedStore());

    watch(openConversationPayload, payload => {
      if (payload?.conversationId || payload?.participantId) {
        currentView.value = DrView.Chat;
      }
    });

    watch(
      () => conversations.value,
      () => {
        pusherStore.setConversationChannels(conversations.value);
      },
      { immediate: true }
    );

    watch(
      () => channels.value.conversations,
      (newChannels: { [key: string]: Channel }, oldChannels: { [key: string]: Channel }) => {
        const newKeys: string[] = Object.keys(newChannels).filter(
          (key: string) => !oldChannels[key]
        );

        newKeys.forEach((key: string) => {
          chatStore.addMessageCreatedListener(newChannels[key]);
          chatStore.addTypingStatusListener(newChannels[key], participants.value, senders.value);
          chatStore.addMessageReadListener(newChannels[key]);
          chatStore.addMessageDeletedListener(newChannels[key]);
        });
      },
      { deep: true }
    );

    watch(
      () => actionPlanVisible.value,
      () => {
        if (currentView.value === DrView.MutualActionPlan && !actionPlanVisible.value) {
          setView(DrView.Presentation);
          useToast().toast.warning(t('MAP_unpublished_explanation'));
        }
      }
    );

    watch(
      () => featureSettings.value,
      () => {
        const isCurrentViewChatDisabled =
          currentView.value === DrView.Chat && featureSettings.value?.disableChat;
        const isCurrentViewFilesDisabled =
          currentView.value === DrView.Files && featureSettings.value?.disableFiles;
        const isCurrentViewMapDisabled =
          currentView.value === DrView.MutualActionPlan && featureSettings.value?.disableActionPlan;

        if (isCurrentViewChatDisabled || isCurrentViewMapDisabled || isCurrentViewFilesDisabled) {
          setView(DrView.Presentation);
        }
      }
    );

    const route = useRoute();
    const router = useRouter();

    const handleRouteQueries = () => {
      const { pageId, email, tracking } = route.query;

      if (pageId) {
        const formattedPageId = String(pageId).split('?')[0];
        const pageExists = visiblePages.value.find(page => page.id === formattedPageId);
        if (pageExists) {
          navigateToPage(String(pageId));
        } else {
          router.replace({ query: { ...route.query, pageId: undefined } });
        }
      }

      if (email) {
        router.replace({ query: { ...route.query, email: undefined } });
      }

      if (tracking) {
        router.replace({ query: { ...route.query, tracking: undefined } });
      }
    };

    const loadPusher = async () => {
      await pusherStore.setDealroomChannel(dsr.value.id);
      addPusherListeners(channels.value.dealroom as Channel);
      chatStore.addConversationCreatedListener(channels.value.dealroom as Channel);
      actionPlanStore.addListeners(channels.value.dealroom as Channel);
      fileStore.addFileUploadedListener(channels.value.dealroom as Channel);

      if (showContextualCommenting.value) {
        addCCPusherListeners();
      }
    };

    const { initialize } = useLaunchDarkly();
    const { loadFeatures } = useLoadFeatures();
    const { features } = storeToRefs(useFeatureStore());

    const isAllowedToInvite = computed(() => {
      const { restricted, threshold } = features.value[Feature.RecipientLimit];
      if (restricted) {
        return participants.value.length < 5;
      }

      if (threshold) {
        return participants.value.length < threshold;
      }

      return true;
    });

    onMounted(async () => {
      await Promise.all([
        loadDSR(),
        revisionsStore.loadRevisions(),
        loadTheme(documentId.value),
        participantsStore.loadParticipants(),
      ]);

      if (process.env.VUE_APP_LAUNCHDARKLY_KEY && entity.value.id) {
        initialize(
          process.env.VUE_APP_LAUNCHDARKLY_KEY,
          {
            kind: LaunchDarklyContextKind.Company,
            key: entity.value.id,
            [AnalyticsCompanyTrait.ID]: entity.value.id,
            [AnalyticsCompanyTrait.Name]: entity.value?.name,
            [AnalyticsCompanyTrait.Plan]: entity.value?.plan,
            [AnalyticsCompanyTrait.CountryCode]: entity.value?.countryCode,
            [AnalyticsCompanyTrait.Language]: entity.value?.language,
          },
          {
            bootstrap: 'localStorage',
            logger: basicLogger({ level: 'error' }),
          }
        );
      }

      loadPusher();
      sessionStore.storeEmail(participant.value.email, participant.value.id);

      setDealroomView(currentView.value);
      setTimeout(() => {
        trackView(currentView.value, currentView.value);
      }, 1000);

      if (dsr.value?.access === AccessRule.Open) {
        await participantsStore.loadUniversalLink();
      }

      //todo move this to the Promise.all above as soon as launch darkly flag in composable has been removed
      await loadFeatures();

      loadingDsrAndParticipants.value = false;

      handleRouteQueries();

      loadParticipantInput();
      loadPosts();
      loadChat();
      actionPlanStore.load();
      fileStore.load();
    });

    watch(
      () => dsr.value?.name,
      (name: string) => {
        const dsrName = name || t('untitled_room');
        window.document.title = `${dsrName} | GetAccept`;
      },
      { immediate: true }
    );

    const navigateToCommentFromURL = async () => {
      const { commentId } = route.query;
      if (commentId) {
        const pageId = viewableComments.value.find(comment => comment.id === commentId)
          ?.editorSelection?.editorBlockId;
        await nextTick();
        navigateToComment({ editorBlockId: pageId, commentId: String(commentId), delayed: true });
      }
    };

    watch(
      () => loadNewContent.value,
      async () => {
        if (!loadNewContent.value) {
          return;
        }
        loadNewContent.value = false;
        ccStore.setComments([]);
        await loadNewPages();

        if (showContextualCommenting.value) {
          resetLockedContentIds();
          await loadComments();
          await nextTick();
          navigateToCommentFromURL();
        }
      }
    );

    const navigateToSection = async ({
      selection,
      pageId,
    }: {
      selection: { sectionId: string };
      pageId: string;
    }) => {
      const sectionId = `view-${selection.sectionId}`;

      if (pageId !== currentPage.value?.id) {
        navigateToPage(pageId);
        await nextTick();
        // TODO: figure out a more reliable way the section can be scrolled to when navigating from another page
        setTimeout(() => {
          document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      } else {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const navigateToPage = async (pageId: string) => {
      setCurrentPage(pageId);
      await nextTick();
      ContentNavigationHelper.scrollToTop('.presentation');
      if (route.query.pageId && pageId !== route.query.pageId) {
        router.replace({ query: { ...route.query, pageId: undefined } });
      }
    };

    const { scrollToComment } = useScrollToComment();

    const navigateToComment = async ({
      editorBlockId,
      commentId,
      delayed = false,
    }: {
      editorBlockId: string;
      commentId: string;
      delayed?: boolean;
    }) => {
      const pageId = visiblePages.value.find(page => page.editorBlock.id === editorBlockId)?.id;
      if (currentPageId.value !== pageId) {
        setCurrentPage(pageId);
        await nextTick();
      }
      setTimeout(
        () => {
          scrollToComment(commentId, false);
        },
        delayed ? 1000 : 0
      );
    };

    const senderFullName = computed(() =>
      participantsStore.owner ? AvatarHelper.getDisplayName(participantsStore.owner) : ''
    );

    const showMobileNavigation = ref(false);

    const handleParticipantCreatedEvent = ({
      createdParticipantId,
    }: {
      createdParticipantId: string;
    }) => {
      if (participantsStore.participants.find(({ id }) => id === createdParticipantId)) {
        return;
      }
      participantsStore.loadParticipants();
    };

    watch(
      () => channels.value.dealroom,
      (dealroomChannel: Channel) => {
        if (dealroomChannel) {
          pusherClient.listen(
            dealroomChannel,
            DealroomEvent.ParticipantCreated,
            handleParticipantCreatedEvent
          );
        }
      }
    );

    const { loadComments } = useContextualCommentingData();

    const ccStore = useContextualCommentingStore();
    const { setCurrentUserId } = ccStore;

    const { editorHasRendered, viewableComments } = storeToRefs(ccStore);

    const showContextualCommenting = computed(
      () =>
        !!dsr.value?.settings?.featureSettings &&
        !dsr.value.settings.featureSettings.disableComments
    );

    watch(
      showContextualCommenting,
      async (doShow: boolean) => {
        if (!doShow) {
          return;
        }
        setCurrentUserId(participantId.value);
        await loadComments();
        await nextTick();
        navigateToCommentFromURL();
      },
      { immediate: true }
    );
    const contentIds = computed(
      () => visiblePages.value.map(page => page?.editorBlock?.contentId) || []
    );
    const {
      lockedContentIds,
      addListeners: addCCPusherListeners,
      resetLockedContentIds,
    } = useContextualCommentingPusher(showContextualCommenting, contentIds);

    const lockedPageIds = computed(() =>
      showContextualCommenting.value
        ? visiblePages.value
            .filter(page => lockedContentIds.value.includes(page.editorBlock.contentId))
            .map(page => page.id)
        : []
    );
    const currentPageLocked = computed(() =>
      showContextualCommenting.value ? lockedPageIds.value.includes(currentPageId.value) : false
    );

    const hasViewableComments = computed(() => viewableComments.value.length > 0);

    const showFloatingComments = computed(
      () => showContextualCommenting.value && editorHasRendered.value && hasViewableComments.value
    );

    const editorContainer = ref(null);
    const { dimensionsUpdating, setNewDimensions } = useDimensionsUpdating(editorContainer);

    watch(currentViewComputed, async (newView, oldView) => {
      if (
        newView === DrView.Presentation &&
        oldView !== DrView.Presentation &&
        showRightSide.value
      ) {
        loadingDimensionsForNewView.value = true;
        await nextTick();
        setNewDimensions(editorContainer.value);
      }
    });

    const loadingDimensionsForNewView = ref(false);

    watch(currentPageId, async () => {
      if (editorContainer.value && showRightSide.value) {
        loadingDimensionsForNewView.value = true;
        await nextTick();
        setNewDimensions(editorContainer.value);
      }
    });

    watch(dimensionsUpdating, () => {
      if (!dimensionsUpdating.value) {
        loadingDimensionsForNewView.value = false;
      }
    });

    const commentsActive = computed(
      () => showRightSide.value && showContextualCommenting.value && hasViewableComments.value
    );

    return {
      loadingInputAndEntity,
      inputOrEntityError,
      loading,
      dsr,
      pages,
      dsrName,
      error,
      currentPage,
      currentPageId,
      participant,
      participants,
      token,
      actionPlans,
      authId,
      dsrCompanyName,
      navigateToSection,
      entity,
      showShareModal,
      ownerCompanyName,
      participantInput,
      senderFullName,
      loadUpdatedContent: revisionsStore.loadUpdatedContent,
      showRevisionPromptBar,
      forcedReviewTimer,
      SlideOrigin,
      DsrConstants,
      showMobileView,
      showRightSide,
      loadingRoom,
      owner,
      showLeftSide,
      nextPageId,
      previousPageId,
      theme,
      updateParticipantInput,
      t,
      participantsOverviewIsVisible,
      navigateToPage,
      loadingDsrAndParticipants,
      loadEditorImageURL,
      universalLink,
      isPublicRoom,
      visiblePages,
      currentView: currentViewComputed,
      setView,
      DrView,
      isChatLoaded,
      openConversationPayload,
      updateFeedVisible,
      hasUnreadPosts,
      setPostsAsSeen,
      showMobileNavigation,

      lockedPageIds,
      currentPageLocked,
      lockedContentIds,
      editorContainer,
      dimensionsUpdating,
      showContextualCommenting,
      showFloatingComments,
      commentsActive,
      navigateToComment,
      scrollToComment,
      loadingDimensionsForNewView,
      actionPlanVisible,
      featureSettings,
      totalFilesToUpload,
      filesUploaded,
      handleInviteSuccess,
      showInviteSuccessModal,
      invitation,
      handleInviteMore,
      handleCloseInviteSuccessModal,
      isAllowedToInvite,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/presentation-content-view';

.dsr-published-app-container {
  height: 100%;

  .loading {
    background-color: var(--gray-98);
  }

  .published-wrapper {
    .revision-prompt-bar {
      text-align: left;
    }

    .main > .presentation > .center-container {
      .editor-wrapper-container {
        display: flex;
        width: 100%;
        gap: var(--spacing-50);

        .editor-wrapper {
          width: 100%;
          max-width: calc($page-width + var(--spacing-200));

          .top-filler {
            height: calc(var(--spacing-200) + var(--spacing-50));
            margin: 0;
            border: none;
          }
        }

        .edit-after-send-notification {
          margin-bottom: var(--spacing-200);
        }
      }
    }
  }
}
</style>
